import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { subDays } from 'date-fns';
import { wiseboardService } from '@/services/wiseboardService';
import { useWiseboardRefresh } from './WiseboardRefreshContext';
import type {
  OfficerMetrics,
  RecruiterMetrics,
  DocumentProcessingMetrics,
  DateFilter
} from '../types/wiseboard';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('PreApprovalMetricsContext', 'wiseboard');

interface MetricsContextType {
  byOfficer: OfficerMetrics[];
  byRecruiter: RecruiterMetrics[];
  byDocumentUser: DocumentProcessingMetrics[];
  isLoading: boolean;
  error: string | null;
  dateFilter: DateFilter;
  setDateFilter: (filter: DateFilter) => void;
  refresh: () => void;
}

const PreApprovalMetricsContext = createContext<MetricsContextType | null>(null);

interface Props {
  children: React.ReactNode;
}

export function PreApprovalMetricsProvider({ children }: Props) {
  const [byOfficer, setByOfficer] = useState<OfficerMetrics[]>([]);
  const [byRecruiter, setByRecruiter] = useState<RecruiterMetrics[]>([]);
  const [byDocumentUser, setByDocumentUser] = useState<DocumentProcessingMetrics[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    start: subDays(new Date(), 30),
    end: new Date(),
    preset: '30d'
  });

  // Batching: collect updates for 200ms before re-rendering
  const pendingUpdates = useRef<unknown[]>([]);
  const batchTimeout = useRef<NodeJS.Timeout | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    log.info('Fetching wiseboard data', {
      startDate: dateFilter.start.toISOString(),
      endDate: dateFilter.end.toISOString(),
      preset: dateFilter.preset
    });

    const result = await wiseboardService.fetchAllData({
      start: dateFilter.start,
      end: dateFilter.end
    });

    if (result.error) {
      log.error('Failed to fetch wiseboard data', { error: result.error.message });
      setError(result.error.message);
    } else {
      setByOfficer(result.byOfficer);
      setByRecruiter(result.byRecruiter);
      setByDocumentUser(result.byDocumentUser);
      log.info('Wiseboard data fetched successfully', {
        officersCount: result.byOfficer.length,
        recruitersCount: result.byRecruiter.length,
        documentUsersCount: result.byDocumentUser.length
      });
    }

    setIsLoading(false);
  }, [dateFilter]);

  // Realtime subscription with batching
  useEffect(() => {
    log.info('Setting up real-time subscription');

    const channel = wiseboardService.subscribeToPreApprovals((payload) => {
      log.info('Received real-time update', { eventType: payload.eventType });

      pendingUpdates.current.push(payload);

      // Debounce updates - wait 200ms for more updates before refreshing
      if (!batchTimeout.current) {
        batchTimeout.current = setTimeout(() => {
          log.info('Processing batched updates', { count: pendingUpdates.current.length });

          // Refresh data with fresh query
          fetchData();

          // Clear batch
          pendingUpdates.current = [];
          batchTimeout.current = null;
        }, 200);
      }
    });

    // Initial data fetch
    fetchData();

    // Cleanup
    return () => {
      log.info('Cleaning up real-time subscription');
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      channel.unsubscribe();
    };
  }, [fetchData]);

  const { registerRefresh } = useWiseboardRefresh();
  useEffect(() => registerRefresh(fetchData), [registerRefresh, fetchData]);

  const contextValue: MetricsContextType = {
    byOfficer,
    byRecruiter,
    byDocumentUser,
    isLoading,
    error,
    dateFilter,
    setDateFilter,
    refresh: fetchData
  };

  return (
    <PreApprovalMetricsContext.Provider value={contextValue}>
      {children}
    </PreApprovalMetricsContext.Provider>
  );
}

export function usePreApprovalMetrics(): MetricsContextType {
  const context = useContext(PreApprovalMetricsContext);
  if (!context) {
    throw new Error('usePreApprovalMetrics must be used within a PreApprovalMetricsProvider');
  }
  return context;
}

export default PreApprovalMetricsContext;
